/**
 * Web Push subscription helpers.
 *
 * Flow:
 *   1. ensureServiceWorker()  → registers /sw.js (once).
 *   2. getPushStatus()        → { supported, permission, subscribed, publicKey }
 *   3. subscribePush()        → asks browser for permission + creates subscription
 *                               + POSTs it to /api/notifications/subscribe.
 *   4. unsubscribePush()      → tears down browser subscription + DELETE server.
 */
import { api } from "@/lib/api";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function ensureServiceWorker() {
  if (!pushSupported()) return null;
  const existing = await navigator.serviceWorker.getRegistration("/sw.js");
  if (existing) return existing;
  return await navigator.serviceWorker.register("/sw.js");
}

export async function getPushStatus() {
  if (!pushSupported()) {
    return { supported: false, permission: "unsupported", subscribed: false, publicKey: null };
  }
  const reg = await ensureServiceWorker();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  let publicKey = null;
  try {
    const { data } = await api.get("/notifications/vapid-public");
    publicKey = data.public_key;
  } catch (e) { /* backend not ready */ }
  return {
    supported: true,
    permission: Notification.permission,
    subscribed: !!sub,
    publicKey,
  };
}

export async function subscribePush() {
  if (!pushSupported()) throw new Error("Web Push isn't supported on this device.");
  const reg = await ensureServiceWorker();
  if (!reg) throw new Error("Couldn't install the FinVault service worker.");

  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notification permission was denied.");

  const { data: vapid } = await api.get("/notifications/vapid-public");
  if (!vapid.public_key) throw new Error("Push isn't configured on the server yet.");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(vapid.public_key),
    });
  }
  const raw = sub.toJSON();
  await api.post("/notifications/subscribe", {
    endpoint: raw.endpoint,
    keys: raw.keys,
  });
  return sub;
}

export async function unsubscribePush() {
  if (!pushSupported()) return false;
  const reg = await ensureServiceWorker();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try {
    await api.post("/notifications/unsubscribe", { endpoint });
  } catch (e) { /* ignore */ }
  return true;
}
